import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PreferencesTree from '../PreferencesTree';
import {
  useLoadCpvCodes,
  useLoadMp,
  useUpdateMpCpvCodes,
  getUpdateCacheOnUpdateMpCpvCodes
} from 'src/models/matchingProfiles/hooks';
import { notification, Skeleton } from 'src/common';
import { toCpvCodes, toPreferencesTree } from 'src/models/matchingProfiles/helpers';

interface IProps {
  disabled: boolean;
}

export const CpvCodesTree: FC<IProps> = props => {
  const { disabled } = props;
  const { t } = useTranslation();
  const { data: cpvCodesData } = useLoadCpvCodes();
  const cpvCodes = cpvCodesData && cpvCodesData.searchCpvCodes;
  const { data: mp } = useLoadMp();
  const matchingCpvCodes = mp?.cpvCodes;
  const matchingCpvCodesExact = mp?.cpvCodesExact;
  const [updateCpvCodes, { loading: isUpdating }] = useUpdateMpCpvCodes();

  const availableNodes = useMemo(() => (cpvCodes ? toPreferencesTree('cpvCodes', cpvCodes, t) : []), [cpvCodes, t]);

  const updateCheckedKeys = useCallback(
    (cpvCodes: string[], cpvCodesExact: string[]) => {
      mp &&
        updateCpvCodes({
          variables: {
            id: mp.id,
            cpvCodes: toCpvCodes(cpvCodes),
            cpvCodesExact: toCpvCodes(cpvCodesExact)
          },
          update: getUpdateCacheOnUpdateMpCpvCodes(mp.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              cpvCodes,
              cpvCodesExact
            }
          }
        }).catch(() => {
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [mp, t, updateCpvCodes]
  );

  return (
    <>
      {cpvCodes ? (
        <PreferencesTree
          availableNodes={availableNodes}
          checkedKeys={matchingCpvCodes || []}
          checkedKeysExact={matchingCpvCodesExact || []}
          updateCheckedKeys={updateCheckedKeys}
          checkedKeysUpdating={isUpdating}
          cardDescription={t('Tenders.cpvCodesDescription')}
          cardDescription2={t('Tenders.cpvCodesDescription2')}
          disabled={disabled}
          type={'cpvCodes'}
          showKey
          checkStrictly
          enableImport
        />
      ) : (
        <Skeleton loading active />
      )}
    </>
  );
};

export default CpvCodesTree;
